import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { fetchAPIEndpoints } from '@/services/api'
import { APIEndpoint } from '@/types'

const mockEndpoints: APIEndpoint[] = [
  {
    id: 'GET_/v1/users',
    path: '/v1/users',
    method: 'GET',
    description: 'Retrieve a paginated list of users',
    parameters: [
      { name: 'limit', type: 'integer', required: false, description: 'Number of users to return (max 100)', in: 'query' },
      { name: 'offset', type: 'integer', required: false, description: 'Number of users to skip for pagination', in: 'query' },
      { name: 'search', type: 'string', required: false, description: 'Search users by name or email', in: 'query' },
    ],
    responses: [
      { status: 200, description: 'Successfully retrieved users', schema: { type: 'object' } },
      { status: 401, description: 'Authentication required' },
      { status: 403, description: 'Insufficient permissions' }
    ],
    authentication: true
  },
  {
    id: 'POST_/v1/users',
    path: '/v1/users',
    method: 'POST',
    description: 'Create a new user account',
    parameters: [
      { name: 'name', type: 'string', required: true, description: 'Full name of the user', in: 'body' },
      { name: 'email', type: 'string', required: true, description: 'Valid email address', in: 'body' },
      { name: 'role', type: 'string', required: false, description: 'User role (default: user)', in: 'body' },
    ],
    responses: [
      { status: 201, description: 'User created successfully' },
      { status: 400, description: 'Invalid input data' },
      { status: 409, description: 'Email already exists' }
    ],
    authentication: true
  }
]

const quickStartGuide = `
# Quick Start Guide

## Authentication

All API requests require authentication using an API key. Include your API key in the request header:

\`\`\`
X-API-Key: your_api_key_here
\`\`\`

## Base URL

All API requests should be made to:
\`\`\`
https://api.yourdomain.com/v1
\`\`\`

## Rate Limits

- **Free Tier**: 1,000 requests per hour
- **Pro Tier**: 10,000 requests per hour  
- **Enterprise Tier**: 100,000 requests per hour

## Response Format

All responses are returned in JSON format with the following structure:

\`\`\`json
{
  "data": {},
  "meta": {
    "total": 100,
    "limit": 10,
    "offset": 0
  }
}
\`\`\`

## Error Handling

Errors are returned with appropriate HTTP status codes and error details:

\`\`\`json
{
  "error": {
    "code": 400,
    "message": "Invalid request parameters",
    "details": "The 'email' field is required"
  }
}
\`\`\`
`

export default function Documentation() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpoint | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'endpoints'>('overview')

  const { data: endpoints = mockEndpoints, isLoading } = useQuery({
    queryKey: ['endpoints'],
    queryFn: fetchAPIEndpoints,
  })

  const generateCodeExample = (endpoint: APIEndpoint) => {
    const baseUrl = 'https://api.yourdomain.com'
    // const method = endpoint.method.toLowerCase()
    
    // JavaScript/Node.js example
    const jsExample = `
// Using fetch API
const response = await fetch('${baseUrl}${endpoint.path}', {
  method: '${endpoint.method}',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'your_api_key_here'
  }${endpoint.method === 'POST' || endpoint.method === 'PUT' ? ',\n  body: JSON.stringify({\n    // your data here\n  })' : ''}
});

const data = await response.json();
console.log(data);`

    // cURL example
    const curlExample = `curl -X ${endpoint.method} \\
  "${baseUrl}${endpoint.path}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key_here"${endpoint.method === 'POST' || endpoint.method === 'PUT' ? ' \\\n  -d \'{"key": "value"}\'' : ''}`

    return { jsExample, curlExample }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="h-96 bg-gray-200 rounded"></div>
          <div className="lg:col-span-3 h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Documentation</h1>
        <p className="mt-2 text-sm text-gray-700">
          Complete reference for integrating with our API
        </p>
      </div>

      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'overview'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('endpoints')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'endpoints'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          API Reference
        </button>
      </div>

      {activeTab === 'overview' ? (
        <div className="card p-8">
          <div className="prose max-w-none">
            <div dangerouslySetInnerHTML={{ 
              __html: quickStartGuide
                .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto"><code>$2</code></pre>')
                .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm">$1</code>')
                .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 mb-4">$1</h1>')
                .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold text-gray-900 mb-3 mt-6">$1</h2>')
                .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
                .replace(/\n\n/g, '<br><br>')
            }} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Endpoints Sidebar */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Endpoints</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {endpoints.map((endpoint) => (
                <button
                  key={endpoint.id}
                  onClick={() => setSelectedEndpoint(endpoint)}
                  className={`w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors ${
                    selectedEndpoint?.id === endpoint.id ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {endpoint.method}
                    </span>
                    <code className="text-sm text-gray-900">{endpoint.path}</code>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Endpoint Details */}
          <div className="lg:col-span-3">
            {selectedEndpoint ? (
              <div className="space-y-6">
                {/* Endpoint Header */}
                <div className="card p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded ${
                      selectedEndpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      selectedEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      selectedEndpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedEndpoint.method}
                    </span>
                    <code className="text-lg bg-gray-100 px-3 py-1 rounded">
                      {selectedEndpoint.path}
                    </code>
                    {selectedEndpoint.authentication && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        🔒 Auth Required
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">{selectedEndpoint.description}</p>
                </div>

                {/* Parameters */}
                {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                  <div className="card p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Parameters</h3>
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Required
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedEndpoint.parameters.map((param, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                                  {param.name}
                                </code>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {param.type}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  param.required 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {param.required ? 'Required' : 'Optional'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {param.description}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Responses */}
                <div className="card p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Responses</h3>
                  <div className="space-y-3">
                    {selectedEndpoint.responses.map((response, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          response.status >= 200 && response.status < 300 ? 'bg-green-100 text-green-800' :
                          response.status >= 400 && response.status < 500 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {response.status}
                        </span>
                        <span className="text-sm text-gray-700">{response.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Code Examples */}
                <div className="card p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Code Examples</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">JavaScript</h4>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{generateCodeExample(selectedEndpoint).jsExample}</code>
                      </pre>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">cURL</h4>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{generateCodeExample(selectedEndpoint).curlExample}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card p-12 text-center">
                <div className="text-gray-400">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-4" />
                  <p>Select an endpoint to view its documentation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
